import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import withEndlessScroll from '../../HOC/endlessScroll';
import { vippedLimitQuery } from '../../../queries';
import { setError } from '../../../actions';
import Section from '../../Section';
import SectionList from '../../SectionList';
import ItemCard from '../../Cards/ItemCard';
import SectionPlaceholder from '../../Placeholders/SectionPlaceholder';
import ItemCardPlaceholder from '../../Placeholders/ItemCardPlaceholder';
import BreadcrumbsPlaceholder from '../../Placeholders/BreadcrumbsPlaceholder';
import { snakeToCamelCase } from '../../../utils/helpers';

const Breadcrumbs = dynamic(() => import('../../Breadcrumbs'), {
  loading: BreadcrumbsPlaceholder,
  ssr: false
});

const queryLimit = 24;
const vippedListRef = React.createRef();
const EndlessVipItems = withEndlessScroll(SectionList);

const VippedPage = () => {
  const dispatch = useDispatch();
  const [itemId, setItemId] = useState(null);
  const [receivesItems, setReceivesItems] = useState(24);
  const router = useRouter();
  const setCarousel = id => {
    return itemId === id ? null : setItemId(id);
  };
  const adaptFilter = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(router?.query).map(([key, value]) => {
          const newKey = key.replace(/q\[([^\]]*)\]|\[\]/g, '$1');
          const newValue =
            !Number.isNaN(+value) || Array.isArray(value)
              ? value
              : value === 'true';

          return [
            snakeToCamelCase(newKey),
            newKey === 'building_type' ? value : newValue
          ];
        })
      ),
    []
  );

  const vippedQueryVars = {
    filter: {
      scope: 'VIPPED',
      ...adaptFilter
    },
    limit: queryLimit,
    offset: 0
  };
  const { loading, data, error, fetchMore } = useQuery(
    vippedLimitQuery(vippedQueryVars),
    {
      variables: vippedQueryVars,
      ssr: false,
      nextFetchPolicy: 'cache-first'
    }
  );

  const items = data?.items || [];
  const itemsList =
    items &&
    items.map(item => (
      <ItemCard
        key={item.id}
        carousel={true}
        itemId={itemId}
        setCarousel={setCarousel}
        {...item}
      />
    ));

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  useEffect(() => {
    router.beforePopState(({ options }) => {
      // eslint-disable-next-line no-param-reassign
      options.scroll = false;

      return true;
    });

    return () => {
      setReceivesItems(queryLimit);
    };
  }, []);

  return (
    <>
      <Breadcrumbs
        isVipPage
        additionalClasses="breadcrumbs__white"
        cypress="vipped-breadcrumbs"
        text="pages.vip.title"
        link="/"
      />
      {!(loading && !itemsList.length) ? (
        <Section
          sectionAdditionalClasses="section-block section-white"
          noHeader
        >
          <EndlessVipItems
            ref={vippedListRef}
            items={itemsList}
            loading={loading}
            listAdditionalClasses="vip-page-list"
            isStopLoad={receivesItems < queryLimit}
            onLoadMore={() => {
              const currentLength = data.items.length;
              const newVippedQueryVars = {
                limit: queryLimit,
                offset: currentLength,
                filter: {
                  scope: 'VIPPED',
                  ...adaptFilter
                }
              };

              fetchMore({
                query: vippedLimitQuery(newVippedQueryVars),
                variables: newVippedQueryVars,
                notifyOnNetworkStatusChange: true
              }).then(fetchMoreResult => {
                setReceivesItems(fetchMoreResult.data.items.length);
              });
            }}
          >
            {itemsList}
          </EndlessVipItems>
        </Section>
      ) : (
        <SectionPlaceholder noHeader={true}>
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
        </SectionPlaceholder>
      )}
    </>
  );
};

export default VippedPage;
